import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Info } from "lucide-react";
import { useState } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";

const WHAT = msg({ id: "analysis.hint.what", message: "What it is:" });
const WHY = msg({ id: "analysis.hint.why", message: "Why it matters:" });
const FIX = msg({ id: "analysis.fix.label", message: "How to fix:" });

type FindingHintProps = {
  // Подпись кнопки для экранных дикторов, например «Подробнее: Души в минуту».
  label: string;
  // Что это — когда карточка не показывает пояснение сама (короткие плашки).
  what?: string | undefined;
  why: string;
  // Нет — только «почему это важно» (у сильных сторон).
  fix?: string | undefined;
};

// Значок ⓘ у заголовка находки: подробнее, в чём проблема и как исправить. Открывается
// наведением и фокусом, а на телефоне — тапом: у Radix Tooltip касание само не открывает,
// поэтому клик открывает подсказку вручную.
export const FindingHint = ({ label, what, why, fix }: FindingHintProps) => {
  const { _ } = useLingui();
  const [open, setOpen] = useState(false);
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={(event) => {
            // Без этого Radix закрывает подсказку по клику.
            event.preventDefault();
            setOpen(true);
          }}
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-subtle transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-2 focus-visible:outline-accent"
        >
          <Info size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        collisionPadding={16}
        className="flex max-w-sm flex-col gap-1.5 px-3.5 py-3 text-sm leading-snug text-foreground-soft"
      >
        {what !== undefined && (
          <p>
            <span className="font-semibold text-foreground">{_(WHAT)}</span> {what}
          </p>
        )}
        <p>
          <span className="font-semibold text-foreground">{_(WHY)}</span> {why}
        </p>
        {fix !== undefined && (
          <p>
            <span className="font-semibold text-accent">{_(FIX)}</span> {fix}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
};
